import { useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import clsx from 'clsx';
import * as R from 'ramda';

import TaskLocationSelectInput from '@atom/components/common/workOrderDetail/locationsAndAssetsSection/taskLocationModal/TaskLocationSelectInput';
import {
  getInitialOptions,
  mapAutocompleteToOptionsState,
  mapDataToSimpleInput,
} from '@atom/components/common/workOrderDetail/locationsAndAssetsSection/taskLocationUtilities';
import WorkOrdersContext, {
  WorkOrdersInputActionTypes,
} from '@atom/components/workOrders/WorkOrdersContext';
import { TASK_LOCATION_DATA_AUTOCOMPLETE } from '@atom/graph/taskLocation';
import { TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import {
  LocationDataState,
  LocationDataTitle,
  LocationOptionsState,
  TaskLocationAutocomplete,
  TaskLocationSimpleInput,
} from '@atom/types/taskLocation';
import { LocationDataFilterInput } from '@atom/types/work';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import classes from './workOrdersFilters.module.css';

const ALDOT_SELECT_FIELDS = [
  LocationDataTitle.MANAGEMENT_UNIT,
  LocationDataTitle.COUNTY,
  LocationDataTitle.ROAD_CLASS,
  LocationDataTitle.ROUTE,
  LocationDataTitle.DIRECTION,
];

const ALDOT_FILTER_DEFAULTS: LocationDataState = {
  [LocationDataTitle.MANAGEMENT_UNIT]: null,
  [LocationDataTitle.COUNTY]: null,
  [LocationDataTitle.ROAD_CLASS]: null,
  [LocationDataTitle.ROUTE]: null,
  [LocationDataTitle.DIRECTION]: null,
};

const styles = {
  separator: {
    margin: '0 1rem',
    paddingBottom: '0.5rem',
  },
};

const WorkFilterLocationAldotFilters = () => {
  const { workOrdersInputCart, filtersDisabled, dispatch } =
    useContext(WorkOrdersContext);

  const locationFilter = workOrdersInputCart?.location;

  const [options, setOptions] =
    useState<LocationOptionsState>(getInitialOptions());

  const [autocompleteData, { loading }] = useMutation<
    { taskLocationDataAutocomplete: TaskLocationAutocomplete },
    { input: TaskLocationSimpleInput }
  >(TASK_LOCATION_DATA_AUTOCOMPLETE);

  const fieldValues = R.pathOr(
    [],
    ['fields'],
    locationFilter,
  ).reduce<LocationDataState>(
    (acc, field) => ({ ...acc, [field.title]: field.value }),
    {},
  );

  const values: LocationDataState = {
    ...ALDOT_FILTER_DEFAULTS,
    ...fieldValues,
    [LocationDataTitle.START_MILEPOST]: locationFilter?.startMilepost ?? '',
    [LocationDataTitle.END_MILEPOST]: locationFilter?.endMilepost ?? '',
  };

  useEffect(() => {
    const getAutocompleteData = async () => {
      const res = await autocompleteData({
        variables: {
          input: mapDataToSimpleInput(values, true),
        },
      });

      setOptions(
        mapAutocompleteToOptionsState(res?.data?.taskLocationDataAutocomplete),
      );
    };

    getAutocompleteData();
  }, [
    values[LocationDataTitle.MANAGEMENT_UNIT],
    values[LocationDataTitle.COUNTY],
    values[LocationDataTitle.ROAD_CLASS],
    values[LocationDataTitle.ROUTE],
    values[LocationDataTitle.DIRECTION],
  ]);

  const updateValue = (property: LocationDataTitle, value: any) => {
    const updatedValues = { ...values, [property]: value };

    dispatch({
      type: WorkOrdersInputActionTypes.UPDATE_WORK_ORDERS_INPUT_PROPERTY,
      data: {
        property: 'location',
        value: {
          fields: ALDOT_SELECT_FIELDS.filter(
            title => !isNilOrEmpty(updatedValues[title]),
          ).map(title => ({ title, value: updatedValues[title] })),
          startMilepost:
            updatedValues[LocationDataTitle.START_MILEPOST] !== ''
              ? updatedValues[LocationDataTitle.START_MILEPOST]
              : undefined,
          endMilepost:
            updatedValues[LocationDataTitle.END_MILEPOST] !== ''
              ? updatedValues[LocationDataTitle.END_MILEPOST]
              : undefined,
        } as LocationDataFilterInput,
      },
    });
  };

  const handleMilepostChange = (title: LocationDataTitle, value: any) => {
    if (value === '') {
      updateValue(title, '');
      return;
    }

    updateValue(title, Number(value));
  };

  const showRanges =
    !isNilOrEmpty(options[LocationDataTitle.START_MILEPOST]) &&
    !isNilOrEmpty(options[LocationDataTitle.END_MILEPOST]);

  const rangeSubtext = `Provide a range from ${options[LocationDataTitle.START_MILEPOST]} to ${
    options[LocationDataTitle.END_MILEPOST]
  } for accurate filtering.`;

  return (
    <>
      <div className={classes.filterContainer}>
        <TaskLocationSelectInput
          property={LocationDataTitle.MANAGEMENT_UNIT}
          options={options[LocationDataTitle.MANAGEMENT_UNIT]}
          value={values[LocationDataTitle.MANAGEMENT_UNIT]}
          required={false}
          updateValue={updateValue}
          loading={loading}
          disabled={filtersDisabled}
          dataCy="locationManagementUnit_filter"
        />
      </div>
      <div className={classes.filterContainer}>
        <TaskLocationSelectInput
          property={LocationDataTitle.COUNTY}
          options={options[LocationDataTitle.COUNTY]}
          value={values[LocationDataTitle.COUNTY]}
          required={false}
          updateValue={updateValue}
          loading={loading}
          disabled={filtersDisabled}
          dataCy="locationCounty_filter"
        />
      </div>
      <div className={classes.filterContainer}>
        <TaskLocationSelectInput
          property={LocationDataTitle.ROAD_CLASS}
          options={options[LocationDataTitle.ROAD_CLASS]}
          value={values[LocationDataTitle.ROAD_CLASS]}
          required={false}
          updateValue={updateValue}
          loading={loading}
          disabled={filtersDisabled}
          dataCy="locationRoadClass_filter"
        />
      </div>
      <div className={classes.filterContainer}>
        <TaskLocationSelectInput
          property={LocationDataTitle.ROUTE}
          options={options[LocationDataTitle.ROUTE]}
          value={values[LocationDataTitle.ROUTE]}
          required={false}
          updateValue={updateValue}
          loading={loading}
          disabled={filtersDisabled}
          dataCy="locationRoute_filter"
        />
      </div>
      <div className={classes.filterContainer}>
        <TaskLocationSelectInput
          property={LocationDataTitle.DIRECTION}
          options={options[LocationDataTitle.DIRECTION]}
          value={values[LocationDataTitle.DIRECTION]}
          required={false}
          updateValue={updateValue}
          loading={loading}
          disabled={filtersDisabled}
          dataCy="locationDirection_filter"
        />
      </div>
      <div className={clsx(classes.filtersDateRange, classes.filterContainer)}>
        <TextField
          type="number"
          value={values[LocationDataTitle.START_MILEPOST]}
          label="Start & End Milepost"
          onChange={event =>
            handleMilepostChange(
              LocationDataTitle.START_MILEPOST,
              event.target.value,
            )
          }
          disabled={filtersDisabled}
          variant="standard"
          inputLabelColor={colors.neutral.dim}
          data-cy="locationStartMilepost_filter"
        />
        <span style={styles.separator}>to</span>
        <TextField
          type="number"
          value={values[LocationDataTitle.END_MILEPOST]}
          label=""
          onChange={event =>
            handleMilepostChange(
              LocationDataTitle.END_MILEPOST,
              event.target.value,
            )
          }
          disabled={filtersDisabled}
          variant="standard"
          data-cy="locationEndMilepost_filter"
        />
      </div>
      {showRanges && <div className={classes.rangeSubtext}>{rangeSubtext}</div>}
    </>
  );
};

export default WorkFilterLocationAldotFilters;
